import type { editor } from 'monaco-editor';

/** A single-range replace: the model range to replace, and the text to replace it with. */
export interface TextEdit {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
  text: string;
}

/** Where the cursor/selection should land after an edit is applied. */
export interface SelectionResult {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

const LEADING_MARKER = /^(#{1,6}\s+|>\s?|(?:[-*+]|\d+\.)\s+)+/;

/** Wraps the selection in `marker` on both sides — `**bold**`, `_italic_`, `~~strike~~`, `` `code` ``. */
export const wrapSelection = (
  selectedText: string,
  marker: string,
  placeholder: string,
): { text: string; selectStart: number; selectEnd: number } => {
  const body = selectedText || placeholder;
  return {
    text: `${marker}${body}${marker}`,
    selectStart: marker.length,
    selectEnd: marker.length + body.length,
  };
};

/** Prefixes every selected line with `prefix` (or a numbered `N. ` sequence for ordered lists). */
export const prefixLines = (
  selectedText: string,
  makePrefix: (lineIndex: number) => string,
): string => {
  const lines = (selectedText || '').split('\n');
  const source = lines.length > 0 && selectedText !== '' ? lines : [''];
  return source.map((line, index) => `${makePrefix(index)}${line}`).join('\n');
};

/** Replaces any existing heading/list/blockquote marker at the start of a line with a new one (or none, for "Paragraph"). */
export const applyLinePrefix = (lineText: string, newPrefix: string): string =>
  newPrefix + lineText.replace(LEADING_MARKER, '');

/** Strips common inline/line-level markdown syntax back to plain text. */
export const clearFormatting = (text: string): string =>
  text
    .split('\n')
    .map((line) =>
      line
        .replace(LEADING_MARKER, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '$1')
        .replace(/(?<!_)_([^_]+?)_(?!_)/g, '$1')
        .replace(/~~(.+?)~~/g, '$1')
        .replace(/`([^`]+?)`/g, '$1'),
    )
    .join('\n');

export interface LinkOptions {
  text: string;
  url: string;
  title?: string;
  openInNewTab?: boolean;
}

export const buildLinkMarkup = ({ text, url, title, openInNewTab }: LinkOptions): string => {
  if (openInNewTab) {
    const titleAttr = title ? ` title="${escapeHtmlAttr(title)}"` : '';
    return `<a href="${escapeHtmlAttr(url)}"${titleAttr} target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`;
  }
  const titlePart = title ? ` "${title.replace(/"/g, '\\"')}"` : '';
  return `[${text}](${url}${titlePart})`;
};

export interface ImageOptions {
  url: string;
  alt: string;
  title?: string;
  widthPx?: number;
}

export const buildImageMarkup = ({ url, alt, title, widthPx }: ImageOptions): string => {
  if (widthPx && widthPx > 0) {
    const titleAttr = title ? ` title="${escapeHtmlAttr(title)}"` : '';
    return `<img src="${escapeHtmlAttr(url)}" alt="${escapeHtmlAttr(alt)}"${titleAttr} width="${Math.round(widthPx)}">`;
  }
  const titlePart = title ? ` "${title.replace(/"/g, '\\"')}"` : '';
  return `![${alt}](${url}${titlePart})`;
};

const YOUTUBE_PATTERN = /^https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i;
const YOUTUBE_EMBED_PATTERN =
  /^https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{6,})/i;
const VIMEO_PATTERN = /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/i;
const DIRECT_VIDEO_PATTERN = /\.(mp4|webm|ogg)(\?.*)?$/i;

export type VideoEmbedResult =
  | { kind: 'youtube' | 'vimeo'; embedUrl: string }
  | { kind: 'direct'; url: string }
  | { kind: 'unsupported' };

/**
 * Only known-safe, known-embeddable video sources are ever turned into an
 * iframe/video tag — an arbitrary URL is rejected rather than passed
 * through as an iframe src, which is what "safe embed" means here.
 */
export const resolveVideoEmbed = (rawUrl: string): VideoEmbedResult => {
  const url = rawUrl.trim();
  const youtube = url.match(YOUTUBE_PATTERN) ?? url.match(YOUTUBE_EMBED_PATTERN);
  if (youtube) {
    return { kind: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${youtube[1]}` };
  }
  const vimeo = url.match(VIMEO_PATTERN);
  if (vimeo) {
    return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeo[1]}` };
  }
  if (DIRECT_VIDEO_PATTERN.test(url) && /^https:\/\//i.test(url)) {
    return { kind: 'direct', url };
  }
  return { kind: 'unsupported' };
};

export const buildVideoMarkup = (result: VideoEmbedResult): string | null => {
  if (result.kind === 'youtube' || result.kind === 'vimeo') {
    return `<iframe src="${escapeHtmlAttr(result.embedUrl)}" width="560" height="315" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen sandbox="allow-scripts allow-same-origin allow-presentation"></iframe>`;
  }
  if (result.kind === 'direct') {
    return `<video controls width="560" src="${escapeHtmlAttr(result.url)}"></video>`;
  }
  return null;
};

export const buildTableMarkup = (rows: number, cols: number): string => {
  const header = `| ${Array.from({ length: cols }, (_, i) => `Column ${i + 1}`).join(' | ')} |`;
  const separator = `| ${Array.from({ length: cols }, () => '---').join(' | ')} |`;
  const dataRows = Array.from(
    { length: Math.max(rows - 1, 1) },
    () => `| ${Array.from({ length: cols }, () => ' ').join(' | ')} |`,
  );
  return [header, separator, ...dataRows].join('\n');
};

/** A markdown table block (contiguous `| ... |` lines including the `---` separator) surrounding a given line, if any. */
export interface TableBlock {
  startLine: number;
  endLine: number;
  lines: string[];
}

const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line);

export const findTableBlock = (model: editor.ITextModel, atLine: number): TableBlock | null => {
  if (!isTableRow(model.getLineContent(atLine))) return null;

  let start = atLine;
  while (start > 1 && isTableRow(model.getLineContent(start - 1))) start -= 1;

  let end = atLine;
  const lineCount = model.getLineCount();
  while (end < lineCount && isTableRow(model.getLineContent(end + 1))) end += 1;

  const lines: string[] = [];
  for (let i = start; i <= end; i += 1) lines.push(model.getLineContent(i));

  return lines.length >= 2 ? { startLine: start, endLine: end, lines } : null;
};

export const addTableRow = (block: TableBlock): string => {
  const colCount = (block.lines[0].match(/\|/g)?.length ?? 2) - 1;
  const newRow = `| ${Array.from({ length: colCount }, () => ' ').join(' | ')} |`;
  return [...block.lines, newRow].join('\n');
};

export const deleteTableRow = (block: TableBlock, lineInBlock: number): string => {
  // lineInBlock is 0-indexed within block.lines; never remove the header (0) or separator (1).
  if (lineInBlock < 2) return block.lines.join('\n');
  return block.lines.filter((_, index) => index !== lineInBlock).join('\n');
};

export const addTableColumn = (block: TableBlock): string =>
  block.lines
    .map((line, index) => {
      const trimmed = line.replace(/\|\s*$/, '').trimEnd();
      if (index === 1) return `${trimmed} --- |`;
      return `${trimmed}  |`;
    })
    .join('\n');

export const deleteTableColumn = (block: TableBlock, colIndex: number): string =>
  block.lines
    .map((line) => {
      const cells = line.split('|').slice(1, -1);
      cells.splice(colIndex, 1);
      return `|${cells.join('|')}|`;
    })
    .join('\n');

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeHtmlAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;');
}
